/**
 * Small pure formatters shared by the console pages.
 *
 * Kept intentionally thin: every console page renders currency and
 * token counts identically, so centralising the locale + options here
 * avoids drift.
 */

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');
const CNY_VALUE_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const CNY_RATE_4DP_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});
const CNY_MICRO_VALUE_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 6,
  maximumFractionDigits: 6,
});

const SERVER_UTC_TIMESTAMP_RE = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?)$/;

export function parseTimestampMs(value: string): number {
  const trimmed = value.trim();
  const match = SERVER_UTC_TIMESTAMP_RE.exec(trimmed);
  if (match) {
    const [, date, time] = match;
    const normalized = `${date}T${time}`.replace(/\.(\d{3})\d+$/, '.$1');
    return Date.parse(`${normalized}Z`);
  }
  return Date.parse(trimmed);
}

function isValidRate(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function formatCnyMagnitude(cents: number): string {
  return `¥${CNY_VALUE_FORMATTER.format(Math.abs(cents) / 100)}`;
}

function formatCnyMicroMagnitude(micros: number): string {
  return `¥${CNY_MICRO_VALUE_FORMATTER.format(Math.abs(micros) / 1_000_000)}`;
}

/**
 * Format a USD cents value (always-positive display). Pass `signed=true` to
 * render charges as "-$0.12" and credits as "+$5.00". Input is integer
 * cents (the wire format since PR-B.5).
 */
export function formatUsdCents(cents: number, signed = false): string {
  if (!Number.isFinite(cents)) {
    return '—';
  }
  const dollars = cents / 100;
  if (!signed) {
    return USD_FORMATTER.format(Math.abs(dollars));
  }
  const base = USD_FORMATTER.format(Math.abs(dollars));
  if (cents > 0) {
    return `+${base}`;
  }
  if (cents < 0) {
    return `-${base}`;
  }
  return base;
}

/**
 * Format integer RMB cents. This is the tenant-facing primary money display;
 * USD credit remains the ledger unit underneath.
 */
export function formatCnyCents(cents: number, signed = false): string {
  if (!Number.isFinite(cents)) {
    return '—';
  }
  const base = formatCnyMagnitude(cents);
  if (!signed || cents === 0) {
    return base;
  }
  return cents > 0 ? `+${base}` : `-${base}`;
}

/**
 * Convert USD ledger cents into RMB display cents using the configured policy
 * rate. Invalid or missing rates return null so pages render an explicit empty
 * state instead of silently falling back to a hardcoded rate.
 */
export function usdCentsToCnyCents(
  usdCents: number,
  cnyPerUsd: number | null | undefined,
): number | null {
  if (!Number.isFinite(usdCents) || !isValidRate(cnyPerUsd)) {
    return null;
  }
  return Math.round(usdCents * cnyPerUsd);
}

export function usdMicrosToCnyCents(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
): number | null {
  if (!Number.isFinite(usdMicros) || !isValidRate(cnyPerUsd)) {
    return null;
  }
  return Math.round((usdMicros * cnyPerUsd) / 10_000);
}

export function usdMicrosToCnyMicros(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
): number | null {
  if (!Number.isFinite(usdMicros) || !isValidRate(cnyPerUsd)) {
    return null;
  }
  return Math.round(usdMicros * cnyPerUsd);
}

/**
 * Resolve a dual-written micro-USD/cents pair to micro-USD.
 *
 * The `usdMicros !== 0` arm is the historical-row fallback, and it is
 * deliberately kept: the micro-USD columns were added with `DEFAULT 0`, so a
 * pre-cutover balance or ledger row carries `micros = 0` alongside the real
 * amount in cents. Treating that as a true zero would render years of money as
 * nothing. This mirrors `microsFromDual` in `@nadoutong/contracts`.
 *
 * The cost of that arm is an ambiguity: a value that is *genuinely* 0 micros
 * with non-zero cents would be re-inflated to `cents x 10 000` — presenting a
 * fabricated amount. #1656 closed that hole at the source rather than here,
 * because the fallback cannot tell the two cases apart from the outside:
 *
 * - a server that floors an aggregate's cents from that same aggregate's micros
 *   can never emit `micros = 0` with `cents > 0` — `floor(0 / 10000)` is 0. The
 *   contradictory pair was only reachable while cents were summed per row.
 * - the one shape that *omitted* micros — an admin-dashboard seat with no usage
 *   — now emits `costUsdMicros: 0` explicitly, so it no longer reaches this
 *   fallback at all.
 *
 * So on current-shape data the fallback fires only for genuine legacy rows,
 * which is what it is for. Do not "fix" this to a presence check
 * (`usdMicros !== undefined`) without first confirming no legacy row with
 * `micros = 0` and non-zero cents is still readable by the caller.
 */
export function usdMicrosFromCentsFallback(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
): number {
  if (typeof usdMicros === 'number' && Number.isFinite(usdMicros) && usdMicros !== 0) {
    return Math.trunc(usdMicros);
  }
  return Math.trunc(usdCents ?? 0) * 10_000;
}

export function formatUsdCentsAsCny(
  usdCents: number,
  cnyPerUsd: number | null | undefined,
  signed = false,
): string {
  const cnyCents = usdCentsToCnyCents(usdCents, cnyPerUsd);
  return cnyCents === null ? '—' : formatCnyCents(cnyCents, signed);
}

export function formatUsdMicrosAsCny(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
  signed = false,
): string {
  const cnyMicros = usdMicrosToCnyMicros(usdMicros, cnyPerUsd);
  if (cnyMicros === null) {
    return '—';
  }
  const base = formatCnyMicroMagnitude(cnyMicros);
  if (!signed || cnyMicros === 0) {
    return base;
  }
  return cnyMicros > 0 ? `+${base}` : `-${base}`;
}

/**
 * Primary money display for balances, KPI cards, funding capacity, and face
 * values. This intentionally rounds metered USD micros to CNY cents.
 */
export function formatUsdMicrosAsCnyDisplay(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
  signed = false,
): string {
  const cnyCents = usdMicrosToCnyCents(usdMicros, cnyPerUsd);
  return cnyCents === null ? '—' : formatCnyCents(cnyCents, signed);
}

export function formatUsdMicrosOrCentsAsCnyDisplay(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
  cnyPerUsd: number | null | undefined,
  signed = false,
): string {
  return formatUsdMicrosAsCnyDisplay(
    usdMicrosFromCentsFallback(usdMicros, usdCents),
    cnyPerUsd,
    signed,
  );
}

/**
 * Metered spend display for request rows, usage drilldowns, and billing
 * diagnostics where sub-cent precision is part of the evidence.
 */
export function formatUsdMicrosOrCentsAsCnyMetered(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
  cnyPerUsd: number | null | undefined,
  signed = false,
): string {
  return formatUsdMicrosAsCny(usdMicrosFromCentsFallback(usdMicros, usdCents), cnyPerUsd, signed);
}

/**
 * Stored `amountUsdMicros` share for a tokenBilling (or surcharge) line.
 *
 * Zero is a largest-remainder leftover below one micro, not a free
 * dimension — do not hide the row and do not word it as free. The
 * formatter's currency is CNY, so the zero marker is `≈¥0` (an USD
 * surface would be `≈$0`). Non-zero shares go through the metered CNY
 * formatter verbatim; callers must not recompute `tokens × rate`.
 */
export function formatUsdMicrosAsCnyMeteredShare(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
): string {
  if (!Number.isFinite(usdMicros)) {
    return '—';
  }
  if (usdMicros === 0) {
    return isValidRate(cnyPerUsd) ? '≈¥0' : '—';
  }
  return formatUsdMicrosAsCny(usdMicros, cnyPerUsd);
}

/**
 * Back-compat alias for older imports. New UI code should choose Display or
 * Metered explicitly.
 */
export function formatUsdMicrosOrCentsAsCny(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
  cnyPerUsd: number | null | undefined,
  signed = false,
): string {
  return formatUsdMicrosOrCentsAsCnyMetered(usdMicros, usdCents, cnyPerUsd, signed);
}

/**
 * Format decimal USD rates such as model pricing in USD / MTok.
 * These are not cents and must not go through `formatUsdCents`.
 */
export function formatUsdRate(value: number): string {
  if (!Number.isFinite(value)) {
    return '—';
  }
  return USD_FORMATTER.format(value);
}

/**
 * Rate-card CNY precision (billing checklist, gaps #1/#2): 2 decimals is
 * the incumbent per-Mtok display, 4 decimals is the media-price upgrade a
 * card takes when its amounts price below the jiao.
 */
export type CnyRateDecimals = 2 | 4;

/**
 * Decide ONE precision for a whole price card (a SKU's prices cell, or a
 * tier matrix) from every CNY amount it will show, per the backend's
 * display checklist: when every amount is a whole number of jiao (0.1
 * CNY) the incumbent 2-decimal display is exact and stays — ¥28.00,
 * ¥2.80; the moment ANY amount prices below the jiao (¥38.7499, ¥0.105)
 * the whole card upgrades to 4 decimals so its figures stay comparable
 * column-wise — ¥44.5200 rides along at 4 even though it would survive 2.
 * Per-card, not per-line: mixed precisions inside one card is exactly the
 * misreading surface the uniform rule exists to close (¥0.70 next to
 * ¥0.5880 reads as a tenfold difference).
 *
 * The jiao test carries a 1e-7 tolerance: the inputs are float products
 * of rate × multiplier × fx whose representation dirt sits around 1e-13,
 * while real sub-jiao prices differ from a jiao by at least 0.001. Pure —
 * callers pass the exact CNY numbers they will format.
 */
export function cnyRateDecimalsForCard(cnyValues: readonly number[]): CnyRateDecimals {
  for (const value of cnyValues) {
    if (!Number.isFinite(value)) {
      continue;
    }
    const scaledToJiao = value * 10;
    if (Math.abs(scaledToJiao - Math.round(scaledToJiao)) > 1e-7) {
      return 4;
    }
  }
  return 2;
}

/**
 * Format decimal USD rates, such as model pricing in USD / MTok, as the RMB
 * display rate at the configured policy rate. `decimals` defaults to the
 * incumbent 2-decimal display so every legacy call site renders
 * byte-identically; billing-document cards pass the card-wide verdict from
 * `cnyRateDecimalsForCard`.
 */
export function formatCnyRateFromUsdRate(
  usdRate: number | null | undefined,
  cnyPerUsd: number | null | undefined,
  decimals: CnyRateDecimals = 2,
): string {
  if (typeof usdRate !== 'number' || !Number.isFinite(usdRate) || !isValidRate(cnyPerUsd)) {
    return '—';
  }
  const formatter = decimals === 4 ? CNY_RATE_4DP_FORMATTER : CNY_VALUE_FORMATTER;
  return `¥${formatter.format(Math.abs(usdRate * cnyPerUsd))}`;
}

/**
 * Format an integer micro-USD unit rate as a card-precision CNY rate —
 * the tier-matrix cell channel (billing checklist gap #2). Distinct from
 * `formatUsdMicrosAsCny`, the fixed-6-decimal channel the flat per-unit
 * LINES keep: a matrix packs up to a dozen figures into a grid, and the
 * checklist prices its cells at card precision (¥0.0700, ¥8.1900), not at
 * micro precision. Same one-rounding rule: the multiplied micro amount
 * arrives un-rounded and the single rounding happens here on the CNY side.
 */
export function formatUsdMicrosAsCnyRate(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
  decimals: CnyRateDecimals,
): string {
  if (!Number.isFinite(usdMicros) || !isValidRate(cnyPerUsd)) {
    return '—';
  }
  const formatter = decimals === 4 ? CNY_RATE_4DP_FORMATTER : CNY_VALUE_FORMATTER;
  return `¥${formatter.format(Math.abs((usdMicros * cnyPerUsd) / 1_000_000))}`;
}

export function formatDisplayRateHint(cnyPerUsd: number | null | undefined): string {
  if (!isValidRate(cnyPerUsd)) {
    return '—';
  }
  return `1 USD = ¥${cnyPerUsd.toFixed(2)}`;
}

/**
 * Render the server-provided decimal-string USD value with the `$` prefix.
 * Server sends strings like `"5.00"` or `"-1.25"`; we pass them through
 * verbatim so trailing zeros survive the JSON round-trip.
 */
export function formatUsdString(raw: string, signed = false): string {
  if (typeof raw !== 'string' || raw.length === 0) {
    return '—';
  }
  const negative = raw.startsWith('-');
  const magnitude = negative ? raw.slice(1) : raw;
  const formatted = `$${magnitude}`;
  if (!signed) return formatted;
  if (negative) return `-${formatted}`;
  if (magnitude === '0.00') return formatted;
  return `+${formatted}`;
}

export function formatTokens(count: number): string {
  if (!Number.isFinite(count) || count < 0) {
    return '—';
  }
  return TOKEN_FORMATTER.format(Math.floor(count));
}

/**
 * Tenant-visible billing modality derived from `UsageRequestLogEntry.protocol`.
 *
 * Inventory:
 *
 * - Chat: sales-form `ModelProtocol` members `anthropic` / `openai` / `gemini`
 *   (`packages/contracts/src/internal-models.ts`). Router main also keeps
 *   these three as the chat face.
 * - Image: gateway Images wire `openai-images` (`FORMAT_OPENAI_IMAGES`;
 *   console docs call this the "OpenAI Images protocol") is the primary
 *   signal and wins whenever it appears on the wire.
 *   Image SKUs are still registered on sales-form `openai`
 *   (`usage_details.protocol` in `metering-images.test.ts`; `openai_images_v1`
 *   is a profile, not a protocol), so live image-generation rows currently
 *   arrive as `openai` and would otherwise chip as chat.
 *   Fallback: when the protocol maps to chat AND the model/SKU name matches
 *   the Docs.tsx `exampleImageModel` heuristic
 *   (`name.toLowerCase().includes('image')`), chip as image. The wire does
 *   not yet distinguish image sales rows; drop this once the backend emits a
 *   distinct protocol or exposes the profile. Unknown strings stay
 *   unlabelled — the name heuristic is not applied to them.
 * - Video / unit-metered: `grok_video_v1` (issue #130 usage_details verbatim)
 *   and `ark_video_v1` (the other video sales-form member).
 *   Seedance is sold on `ark_video_v1` but served off the openai ladder
 *   (`SERVING_UPSTREAM_PROTOCOL.ark_video_v1 === 'openai'` in
 *   `internal-models.ts`). A log row whose `protocol` is the chat-form
 *   `openai` stays chat — that string is not a second video protocol;
 *   adding it here would chip every OpenAI-family chat SKU. The tenant
 *   log does not expose `lane`, the field that can carry `ark_video_v1`
 *   as an egress value while `protocol` remains `openai`.
 */
export type RequestBillingModality = 'chat' | 'image' | 'video';

const CHAT_PROTOCOLS: ReadonlySet<string> = new Set(['anthropic', 'openai', 'gemini']);
const IMAGE_PROTOCOLS: ReadonlySet<string> = new Set(['openai-images']);
const UNIT_METERED_PROTOCOLS: ReadonlySet<string> = new Set(['grok_video_v1', 'ark_video_v1']);

export function requestBillingModality(
  protocol: string,
  model?: string,
): RequestBillingModality | undefined {
  if (CHAT_PROTOCOLS.has(protocol)) {
    // Docs.tsx `exampleImageModel`: `name.toLowerCase().includes('image')`.
    // Wire does not yet distinguish image sales rows (SKUs registered on
    // openai; `openai_images_v1` is a profile, not a protocol).
    if (typeof model === 'string' && model.toLowerCase().includes('image')) {
      return 'image';
    }
    return 'chat';
  }
  if (IMAGE_PROTOCOLS.has(protocol)) return 'image';
  if (UNIT_METERED_PROTOCOLS.has(protocol)) return 'video';
  return undefined;
}

export function isUnitMeteredProtocol(protocol: string | undefined): boolean {
  return typeof protocol === 'string' && UNIT_METERED_PROTOCOLS.has(protocol);
}

/**
 * #1670 / #2160 / tenant#138: unit-LANE request rows carry structurally
 * zero token counters — billed per unit, not per token. Rendering `0`
 * there reads as "free tokens"; the em dash says "not a token request".
 *
 * Discriminator (ONE place, used by Requests / Optimization / member
 * request panels). `kind` is the only thing that may hide token counters;
 * presence of `unitBilling` is not:
 *
 * - `kind: 'charged'` / `'not_charged'` → unit-lane, dash the token cells.
 * - `kind: 'surcharge'` → token-lane plus additive unit shares. Token
 *   counters stay visible (a mixed row that dashes them hides the body's
 *   charge). The #133 presence check treated this arm as unit-metered.
 * - `unitBilling` absent but `protocol` is a known unit-metered video
 *   protocol → still dash, unless `tokenBilling` is present. The
 *   projector used to omit `unitBilling` when a Grok video carried two
 *   unit dimensions (seconds + per-image); the counters on those rows
 *   stay structurally zero. `ark_video_v1` is also on that protocol
 *   list, but seedance bills on the token lane — a present `tokenBilling`
 *   is the token-lane proof and must keep the counters.
 *
 * Token rows (neither signal) get the pre-formatted value untouched.
 */
function readUnitBillingKind(unitBilling: unknown): string | undefined {
  if (typeof unitBilling !== 'object' || unitBilling === null) {
    return undefined;
  }
  if (!('kind' in unitBilling)) {
    return undefined;
  }
  const kind = unitBilling.kind;
  return typeof kind === 'string' ? kind : undefined;
}

export function isUnitMeteredRequestRow(row: {
  readonly unitBilling?: unknown;
  readonly protocol?: string;
  readonly tokenBilling?: unknown;
}): boolean {
  const kind = readUnitBillingKind(row.unitBilling);
  if (kind === 'charged' || kind === 'not_charged') {
    return true;
  }
  if (kind === 'surcharge') {
    return false;
  }
  if (row.tokenBilling !== undefined) {
    return false;
  }
  return isUnitMeteredProtocol(row.protocol);
}

export function unitAwareTokenCell(
  row: {
    readonly unitBilling?: unknown;
    readonly protocol?: string;
    readonly tokenBilling?: unknown;
  },
  formatted: string,
): string {
  return isUnitMeteredRequestRow(row) ? '—' : formatted;
}

/**
 * Render an ISO-8601 timestamp in the browser's local time.
 * Falls back to the raw string on parse failure.
 */
export function formatTimestamp(iso: string | null | undefined): string {
  if (typeof iso !== 'string' || iso.length === 0) {
    return '—';
  }
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) {
    return iso;
  }
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * Whole days from now until an expiry, floored, or `null` when the input
 * is not a readable timestamp.
 *
 * Floored, not rounded: a lot with 6.9 days left reads as "6 days", which
 * tells the tenant to act a day sooner than reality rather than a day
 * later. Negative values are possible and meaningful — the gift-expiry
 * sweep runs hourly, so a lot can sit expired-but-not-yet-clawed-back for
 * up to an hour; callers render that as "expired", not as a countdown.
 */
export function daysUntil(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (typeof iso !== 'string' || iso.length === 0) {
    return null;
  }
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) {
    return null;
  }
  return Math.floor((ms - now.getTime()) / 86_400_000);
}

/**
 * Back-compat alias for `formatUsdCents`. Keeps call sites that still
 * reference `formatUsd` compiling after PR-B.5's cents migration. New code
 * should prefer `formatUsdCents` or `formatUsdString` directly.
 */
export const formatUsd = formatUsdCents;
